import type { ReactNode } from 'react';
import React from 'react';

import type { User } from '../@types/contactInteraction';
import { ContactInteractionType } from '../@types/contactInteraction';
import type { Contact, Owner } from '../@types/Owner';

// Define complete interfaces for interactions to fix type issues
interface BaseInteraction {
  id: number;
  interactionType: string;
  interactionAt: string;
  interactionWith: User | null;
  meetingAt?: string | null;
  meetingWith?: User | null;
  comment?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

interface QualityReferenceInteractionType extends BaseInteraction {
  interactionType: ContactInteractionType;
}

interface RentManagerInteractionType extends BaseInteraction {
  clientType?: 'professional' | 'individual';
  duration?: string | null;
}

interface SalesManagerInteractionType extends BaseInteraction {
  amount?: string | null;
}

interface LeadInteractionType extends BaseInteraction {
  rgpdDocumentSigned?: boolean;
  rgpdDocumentUrl?: string | null;
  rgpdProofUrl?: string | null;
}

// Unified contact type that can represent both Contact and different types of interactions
export interface UnifiedContact {
  id: number;
  type: string;
  createdAt?: string;
  updatedAt?: string;
  user?: Partial<User>;
  interactionAt?: string;
  meetingAt?: string | null;
  meetingWith?: User | null;
  comment?: string | null;
  source: 'contact' | 'qualityReference' | 'rentManager' | 'salesManager' | 'lead';
}

export function getMeetingTypeInfo(contactType: string): { emoji: ReactNode; label: string } {
  switch (contactType) {
    case ContactInteractionType.PHONE:
      return { emoji: (<span role="img" aria-label="telephone">📞</span>), label: 'Téléphone' };
    case ContactInteractionType.VISIO:
      return { emoji: (<span role="img" aria-label="laptop computer">💻</span>), label: 'Visio' };
    case ContactInteractionType.PHYSICAL:
      return { emoji: (<span role="img" aria-label="handshake">🤝</span>), label: 'Présentiel' };
    case ContactInteractionType.PAPER:
    case ContactInteractionType.PAPER_FOR_CONSENT:
      return { emoji: (<span role="img" aria-label="document">📄</span>), label: 'Courrier' };
    case ContactInteractionType.MAIL:
    case ContactInteractionType.MAIL_FOR_CONSENT:
    case ContactInteractionType.MAIL_FOLLOW_UP:
      return { emoji: (<span role="img" aria-label="email">📧</span>), label: 'Email' };
    default:
      return { emoji: (<span role="img" aria-label="calendar">📅</span>), label: 'Rendez-vous' };
  }
}

/**
 * Merges regular contacts and various interaction types from an Owner into a single unified list
 * for displaying in contact history components
 */
export function getMergedContactHistory(owner: Owner): UnifiedContact[] {
  const contacts: UnifiedContact[] = [];

  // Add regular contacts
  if (owner.contacts && owner.contacts.length > 0) {
    contacts.push(
      ...owner.contacts.map((contact: Contact): UnifiedContact => ({
        id: contact.id,
        type: contact.type,
        createdAt: contact.createdAt,
        updatedAt: contact.updatedAt,
        user: contact.user,
        source: 'contact',
      })),
    );
  }

  // Add quality reference interactions
  if (owner.qualityReferenceInteractions && owner.qualityReferenceInteractions.length > 0) {
    const typedInteractions = owner.qualityReferenceInteractions as unknown as QualityReferenceInteractionType[];
    contacts.push(
      ...typedInteractions.map((interaction): UnifiedContact => ({
        id: interaction.id,
        type: interaction.interactionType || 'undefined',
        createdAt: interaction.createdAt || interaction.interactionAt,
        updatedAt: interaction.updatedAt || undefined,
        user: interaction.interactionWith || undefined,
        interactionAt: interaction.interactionAt,
        meetingAt: interaction.meetingAt,
        meetingWith: interaction.meetingWith || undefined,
        comment: interaction.comment || undefined,
        source: 'qualityReference',
      })),
    );
  }

  // Add rent manager interactions
  if (owner.rentManagerInteractions && owner.rentManagerInteractions.length > 0) {
    const typedInteractions = owner.rentManagerInteractions as unknown as RentManagerInteractionType[];
    contacts.push(
      ...typedInteractions.map((interaction): UnifiedContact => ({
        id: interaction.id,
        type: interaction.interactionType || 'undefined',
        createdAt: interaction.createdAt || interaction.interactionAt,
        updatedAt: interaction.updatedAt || undefined,
        user: interaction.interactionWith || undefined,
        interactionAt: interaction.interactionAt,
        meetingAt: interaction.meetingAt,
        meetingWith: interaction.meetingWith || undefined,
        comment: interaction.comment || undefined,
        source: 'rentManager',
      })),
    );
  }

  // Add sales manager interactions
  if (owner.salesManagerInteractions && owner.salesManagerInteractions.length > 0) {
    const typedInteractions = owner.salesManagerInteractions as unknown as SalesManagerInteractionType[];
    contacts.push(
      ...typedInteractions.map((interaction): UnifiedContact => ({
        id: interaction.id,
        type: interaction.interactionType || 'undefined',
        createdAt: interaction.createdAt || interaction.interactionAt,
        updatedAt: interaction.updatedAt || undefined,
        user: interaction.interactionWith || undefined,
        interactionAt: interaction.interactionAt,
        meetingAt: interaction.meetingAt,
        meetingWith: interaction.meetingWith || undefined,
        comment: interaction.comment || undefined,
        source: 'salesManager',
      })),
    );
  }

  // Add lead interactions
  if (owner.leadInteractions && owner.leadInteractions.length > 0) {
    const typedInteractions = owner.leadInteractions as unknown as LeadInteractionType[];
    contacts.push(
      ...typedInteractions.map((interaction): UnifiedContact => ({
        id: interaction.id,
        type: 'lead',
        createdAt: interaction.createdAt || interaction.interactionAt,
        updatedAt: interaction.updatedAt || undefined,
        user: interaction.interactionWith || undefined,
        interactionAt: interaction.interactionAt,
        comment: interaction.comment || undefined,
        source: 'lead',
      })),
    );
  }

  // Sort the contacts by date (most recent first)
  return contacts.sort((a, b) => {
    const dateA = new Date(a.createdAt || 0).getTime();
    const dateB = new Date(b.createdAt || 0).getTime();
    return dateB - dateA;
  });
}

export function getNextScheduledMeeting(owner: Owner): {
  meetingAt: string;
  meetingWith?: User;
  source: string;
} | null {
  const allMeetings: Array<{ meetingAt: string; meetingWith?: User; source: string }> = [];

  if (owner.qualityReferenceInteractions) {
    const typedInteractions = owner.qualityReferenceInteractions as unknown as QualityReferenceInteractionType[];
    typedInteractions.forEach((interaction) => {
      if (interaction.meetingAt) {
        allMeetings.push({
          meetingAt: interaction.meetingAt,
          meetingWith: interaction.meetingWith,
          source: 'Référent mutation location',
        });
      }
    });
  }

  if (owner.salesManagerInteractions) {
    const typedInteractions = owner.salesManagerInteractions as unknown as SalesManagerInteractionType[];
    typedInteractions.forEach((interaction) => {
      if (interaction.meetingAt) {
        allMeetings.push({
          meetingAt: interaction.meetingAt,
          meetingWith: interaction.meetingWith,
          source: 'Transactionnaire',
        });
      }
    });
  }

  if (owner.rentManagerInteractions) {
    const typedInteractions = owner.rentManagerInteractions as unknown as RentManagerInteractionType[];
    typedInteractions.forEach((interaction) => {
      if (interaction.meetingAt) {
        allMeetings.push({
          meetingAt: interaction.meetingAt,
          meetingWith: interaction.meetingWith,
          source: 'Gestionnaire locatif',
        });
      }
    });
  }

  const now = new Date().getTime();
  const futureMeetings = allMeetings
    .filter((meeting) => new Date(meeting.meetingAt).getTime() > now)
    .sort((a, b) => new Date(a.meetingAt).getTime() - new Date(b.meetingAt).getTime());

  return futureMeetings.length > 0 ? futureMeetings[0] : null;
}


